/**
 * Composable per Purchase Orders By Customer autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { PurchaseOrdersByCustomerOptions } from '@/lib/autocompletes';
import type { PurchaseOrderAutocompleteResult } from '@/lib/models/purchaseOrder';
import { getPurchaseOrdersByCustomerAutocomplete } from '@/lib/autocompletes/purchaseOrdersByCustomer';

export function usePurchaseOrdersByCustomerAutocomplete(
	options: BaseAutocompleteComposableOptions = {},
) {
	const purchaseOrders = ref<PurchaseOrderAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	// Usa withLoading iniettato dal parent, fallback su istanza locale se non fornito
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchPurchaseOrdersByCustomer(
		options: PurchaseOrdersByCustomerOptions,
	): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getPurchaseOrdersByCustomerAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					purchaseOrders.value = result;
				}
			} catch (err) {
				console.error(
					'❌ ERROR usePurchaseOrdersByCustomerAutocomplete.searchPurchaseOrdersByCustomer:',
					err,
				);
				error(
					'Errore durante il caricamento delle Commesse Business Central! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					purchaseOrders.value = [];
				}
			}
		}, 'searchPurchaseOrdersByCustomer');
	}

	function clearPurchaseOrders(): void {
		purchaseOrders.value = [];
	}

	return {
		purchaseOrders: readonly(purchaseOrders),
		searchPurchaseOrdersByCustomer,
		clearPurchaseOrders,
	};
}
